import type {ReactNode} from 'react'
import {ActionList, ActionMenu, RelativeTime} from '@primer/react'
import {FilterIcon} from '@primer/octicons-react'
import {SinceSource} from '../../../routes/route-payload-types'
import {SidebarSection} from '../SidebarSection'
import {SidebarDisclosureHeader} from '../SidebarDisclosureHeader'
import {useEffectiveSelection} from '../data/use-effective-selection'
import {usePullRequestAnalytics} from '../../../hooks/use-pull-request-analytics'

const sinceSourceDescriptionMap: Record<SinceSource, string> = {
  [SinceSource.Created]: 'Since this pull request was created',
  [SinceSource.LastReview]: 'Since your last review',
  [SinceSource.LastInteracted]: 'Since you last interacted',
  [SinceSource.ReadyForReview]: 'Since marked ready for review',
}

export function ActivitySection({children}: {children: ReactNode}) {
  const {sinceSelectors, effectiveSelection, handleSelect} = useEffectiveSelection()
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()

  // Fall back to a static, non-interactive header when there is nothing to switch between.
  if (!effectiveSelection || sinceSelectors.length === 0) {
    return <SidebarSection title="Activity">{children}</SidebarSection>
  }

  const header = (
    <ActionMenu>
      <ActionMenu.Anchor>
        <SidebarDisclosureHeader
          title="Activity"
          trailingIcon={FilterIcon}
          aria-label={`Filter activity: ${sinceSourceDescriptionMap[effectiveSelection.source]}`}
        />
      </ActionMenu.Anchor>
      <ActionMenu.Overlay align="end">
        <ActionList selectionVariant="single">
          {sinceSelectors.map(selector => (
            <ActionList.Item
              key={selector.source}
              selected={selector.source === effectiveSelection.source}
              onSelect={() => {
                sendPullRequestAnalyticsEvent('overview.activity_filter_change', 'OVERVIEW_ACTIVITY_SELECTOR', {
                  source: selector.source,
                })
                handleSelect(selector)
              }}
            >
              {sinceSourceDescriptionMap[selector.source]}
              <ActionList.Description>
                <RelativeTime date={new Date(selector.timestamp)} />
              </ActionList.Description>
            </ActionList.Item>
          ))}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )

  return (
    <SidebarSection title="Activity" header={header}>
      {children}
    </SidebarSection>
  )
}
